/**
 * MeetingService
 * Service for Meeting entity operations
 * Route: api/meetings
 * Controller: MeetingsController.cs
 */
import { CrudService } from '../base/CrudService';
/**
 * MeetingService
 * Handles meeting CRUD operations and custom endpoints
 * Maps to /api/meetings endpoint
 */
export class MeetingService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? MeetingService.DEFAULT_SERVICE_PATH,
            basePath: MeetingService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== CUSTOM METHODS ====================
    /**
     * Get meeting with enriched model view by ID
     * GET /api/meetings/view/{id}
     * @param id - Meeting ID
     * @returns Enriched meeting model view
     */
    async getViewById(id) {
        const response = await this.getViewByIdRaw(id);
        if (!response.data) {
            throw new Error(`Meeting view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get meeting view by ID (raw)
     * @param id - Meeting ID
     * @returns ResponseData with enriched model view
     */
    getViewByIdRaw(id) {
        const endpoint = this.getEndpoint('view', `view/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get all meetings with enriched data (ModelView)
     * GET /api/meetings/view
     * @returns Array of enriched meeting model views
     */
    async getAllViews() {
        const response = await this.getAllViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all meeting views (raw)
     * @returns ResponseData with array of enriched model views
     */
    getAllViewsRaw() {
        const endpoint = this.getEndpoint('allViews', 'view');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get meetings by organizer with enriched data (ModelView)
     * GET /api/meetings/organizer/{organizerId}/view
     * @param organizerId - Organizer ID
     * @returns Array of enriched meeting model views
     */
    async getViewsByOrganizer(organizerId) {
        const response = await this.getViewsByOrganizerRaw(organizerId);
        return response.data ?? [];
    }
    /**
     * Get meeting views by organizer (raw)
     * @param organizerId - Organizer ID
     * @returns ResponseData with array of enriched model views
     */
    getViewsByOrganizerRaw(organizerId) {
        const endpoint = this.getEndpoint('viewsByOrganizer', `organizer/${organizerId}/view`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get filtered meetings with enriched data (ModelView)
     * POST /api/meetings/list-view
     * @param filter - Optional filter criteria
     * @returns Array of enriched meeting model views
     */
    async getFilteredViews(filter) {
        const response = await this.getFilteredViewsRaw(filter);
        return response.data ?? [];
    }
    /**
     * Get filtered meeting views (raw)
     * @param filter - Optional filter criteria
     * @returns ResponseData with array of enriched model views
     */
    getFilteredViewsRaw(filter) {
        const endpoint = this.getEndpoint('filteredViews', 'list-view');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, filter || {});
    }
}
MeetingService.DEFAULT_SERVICE_PATH = 'meetings';
MeetingService.BASE_PATH = 'api/meetings';
//# sourceMappingURL=MeetingService.js.map